// Customer account view. Consolidated cta cte view for a single customer.
// Header shows the customer's name + a big balance pill. Below it: the
// chronological list of cta cte sales and legacy deliveries.
//
// The list is intentionally chronological in the BACKEND response (sales
// and movements returned separately). We merge them here for a single
// stream so the operator can read the history top to bottom.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCustomerAccount, useCreateAccountSettlement, fullName } from "./hooks";
import { usePaymentMethods } from "../payment-methods/hooks";
import { Card } from "../../components/Card";
import { Chip } from "../../components/Chip";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { MoneyInput } from "../../components/MoneyInput";
import { Sheet } from "../../components/Sheet";
import { PageHeader } from "../../components/PageHeader";
import { Logo } from "../../components/Logo";
import { ArrowLeftRightIcon, BanknoteIcon, CreditCardIcon, NotebookPenIcon, PlusIcon, UserIcon } from "../../components/icons";
import { PaymentMethodButton } from "../../components/PaymentMethodButton";
import type { PaymentMethod } from "../payment-methods/api";
import { formatARS } from "../../lib/money";
import type { AccountCollection, CustomerAccount } from "./api";
import {
  allocationTarget,
  completeCollectionLine,
  emptyCollectionLine,
  evaluateCollectionAllocations,
  formatCollectionAmount,
  previewCollectionLine,
  type CollectionLineDraft,
  type CollectionTarget,
} from "./collection-state";

export function CustomerAccountView() {
  const { id } = useParams<{ id: string }>();
  const customerId = id ? Number(id) : null;
  const accountQ = useCustomerAccount(customerId);
  const [collectSheetOpen, setCollectSheetOpen] = useState(false);
  const [collectTargetSaleId, setCollectTargetSaleId] = useState<number | null>(null);

  // Build the merged timeline unconditionally so the hook order is stable
  // across the loading -> loaded transition. (Rules of Hooks: hooks must
  // be called in the same order every render.)
  const sales = accountQ.data?.sales ?? [];
  const movements = accountQ.data?.movements ?? [];
  const collections = accountQ.data?.collections ?? [];
  const timeline = useMemo(() => {
    type Entry =
      | {
          kind: "sale";
          at: string;
          data: (typeof sales)[0];
        }
      | {
          kind: "movement";
          at: string;
          data: (typeof movements)[0];
        }
      | {
          kind: "collection";
          at: string;
          data: (typeof collections)[0];
        };
    const entries: Entry[] = [
      ...sales.map((s) => ({ kind: "sale" as const, at: s.createdAt, data: s })),
      ...movements.map((m) => ({ kind: "movement" as const, at: m.occurredAt, data: m })),
      ...collections.map((collection) => ({ kind: "collection" as const, at: collection.occurredAt, data: collection })),
    ];
    entries.sort((a, b) => (a.at < b.at ? 1 : -1));
    return entries;
  }, [sales, movements, collections]);

  if (accountQ.isLoading) {
    return (
      <main style={centerStyle}>
        <Logo height={32} />
      </main>
    );
  }
  if (!accountQ.data) {
    return (
      <main style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        <PageHeader
          title="Cliente"
          backHref="/customers"
        />
        <p style={{ padding: "var(--space-4)" }}>No se encontró el cliente.</p>
      </main>
    );
  }

  const { customer, balance } = accountQ.data;
  const name = fullName(customer);
  const pendingSales = accountQ.data.pendingSales ?? [];
  const historicalAllocation = accountQ.data.historicalAllocation ?? {
    unallocatedCustomerCreditCents: 0,
  };
  const hasHistoricalMovements = balance.paidCents > 0;

  return (
    <main className="page page--narrow">
      <PageHeader
        title="Cuenta"
        backHref="/customers"
      />

      <div
        className="customer-account"
        style={{
          padding: "var(--space-3) var(--space-4)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
          // FAB "Registrar movimiento" is fixed bottom-right. Reserve
          // enough space so the last timeline card is never covered.
          paddingBottom: "calc(140px + env(safe-area-inset-bottom))",
        }}
      >
        <Card className="account-identity">
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
            <div style={{ fontSize: "var(--font-size-28)", fontWeight: 600, letterSpacing: "-0.01em" }}>
              {name}
            </div>
            {customer.phone && (
              <div style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
                {customer.phone}
              </div>
            )}
            {customer.email && (
              <div style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
                {customer.email}
              </div>
            )}
          </div>
        </Card>

        <Card className={`account-balance ${balance.balanceCents > 0 ? "is-debt" : balance.balanceCents < 0 ? "is-credit" : "is-ok"}`}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
            <div
              style={{
                fontSize: "var(--font-size-12)",
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 500,
              }}
            >
              Saldo
            </div>
            <div
              className="num"
              style={{
                fontSize: "var(--font-size-48)",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: balanceLabelColor(balance.balanceCents),
                lineHeight: 1.1,
              }}
            >
              {balance.balanceCents > 0
                ? formatARS(balance.balanceCents)
                : balance.balanceCents < 0
                  ? `−${formatARS(-balance.balanceCents)}`
                  : formatARS(0)}
            </div>
            <div style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
              {balance.balanceCents > 0
                ? pendingSales.length > 0
                  ? `Debe — ${pendingSales.length} venta${pendingSales.length === 1 ? "" : "s"} pendiente${pendingSales.length === 1 ? "" : "s"}`
                  : "Debe — ajuste histórico pendiente"
                : balance.balanceCents < 0
                  ? `Saldo a favor`
                  : `Al día`}
            </div>
            <div
              style={{
                marginTop: "var(--space-2)",
                display: "flex",
                gap: "var(--space-4)",
                flexWrap: "wrap",
              }}
            >
              <Small label="Facturado" value={formatARS(balance.totalChargedCents)} />
              <Small label="Entregado" value={formatARS(balance.paidCents)} />
            </div>
          </div>
        </Card>

        <section className="account-pending-sales" aria-labelledby="pending-sales-heading">
          <div className="account-pending-heading">
            <div>
              <p className="eyebrow">Cuenta corriente</p>
              <h2 id="pending-sales-heading">Ventas pendientes</h2>
            </div>
            <span>{pendingSales.length} {pendingSales.length === 1 ? "venta" : "ventas"}</span>
          </div>

          {hasHistoricalMovements && (
            <p className="account-historical-notice">
              Los pagos anteriores se muestran distribuidos por antigüedad. Esta proyección no modifica registros.
            </p>
          )}

          {pendingSales.length === 0 ? (
            <Card className="account-pending-empty">No hay ventas pendientes de cobro.</Card>
          ) : (
            <div className="account-pending-list">
              {pendingSales.map((sale) => (
                <Card className="account-pending-sale" key={sale.saleId}>
                  <div className="account-pending-sale__header">
                    <div>
                      <div className="account-pending-sale__title">
                        <strong>Venta #{sale.saleId}</strong>
                        <Chip variant="tag-warning" size="sm">Pendiente</Chip>
                      </div>
                      <small>
                        {new Date(sale.createdAt).toLocaleDateString("es-AR")} · {sale.itemsCount} {sale.itemsCount === 1 ? "ítem" : "ítems"}
                      </small>
                    </div>
                    <strong className="account-pending-sale__amount num">{formatARS(sale.pendingCents)}</strong>
                  </div>
                  <dl className="account-pending-sale__breakdown">
                    <div><dt>Original</dt><dd className="num">{formatARS(sale.originalCreditCents)}</dd></div>
                    <div><dt>Abonado</dt><dd className="num">{formatARS(sale.totalAppliedCents)}</dd></div>
                    {sale.discountCents > 0 && <div><dt>Descuento</dt><dd className="num">{formatARS(sale.discountCents)}</dd></div>}
                    <div><dt>Pendiente</dt><dd className="num">{formatARS(sale.pendingCents)}</dd></div>
                  </dl>
                  <Button
                    type="button"
                    variant="outline"
                    className="account-pending-sale__collect"
                    onClick={() => {
                      setCollectTargetSaleId(sale.saleId);
                      setCollectSheetOpen(true);
                    }}
                  >
                    Cobrar
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {historicalAllocation.unallocatedCustomerCreditCents > 0 && (
            <p className="account-unallocated-credit">
              Saldo histórico a favor del cliente: <strong className="num">{formatARS(historicalAllocation.unallocatedCustomerCreditCents)}</strong>.
            </p>
          )}
        </section>

        <div
          className="account-history-heading"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--space-2)",
            marginTop: "var(--space-2)",
          }}
        >
          <div><strong style={{ fontSize: "var(--font-size-14)" }}>Historial</strong><span className="account-history-subtitle">Ventas, cobros y sus medios registrados.</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-13)" }}>
              Total facturado {formatARS(balance.totalChargedCents)} · Total abonado {formatARS(balance.paidCents)}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {timeline.length === 0 && (
            <p style={{ color: "var(--text-secondary)" }}>Sin movimientos todavía.</p>
          )}
          {timeline.map((entry) => {
            if (entry.kind === "sale") {
              const s = entry.data;
              const voided = s.status === "voided";
              return (
                <Card
                  className={`account-event account-event--sale${voided ? " is-voided" : ""}`}
                  key={`s${s.id}`}
                  style={{
                    padding: "var(--space-3) var(--space-4)",
                    opacity: voided ? 0.55 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "var(--space-3)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-2)",
                          flexWrap: "wrap",
                        }}
                      >
                        <strong>Venta #{s.id}</strong>
                        <Chip variant="tag" size="sm">
                          Cta. cte.
                        </Chip>
                        {voided && (
                          <Chip variant="tag-danger" size="sm">
                            Anulada
                          </Chip>
                        )}
                      </div>
                      <div
                        style={{
                          color: "var(--text-secondary)",
                          fontSize: "var(--font-size-13)",
                        }}
                      >
                        {new Date(s.createdAt).toLocaleString("es-AR")} · {s.items.length}{" "}
                        {s.items.length === 1 ? "ítem" : "ítems"}
                      </div>
                      {s.items.map((it) => (
                        <div
                          key={it.id}
                          style={{ fontSize: "var(--font-size-13)" }}
                        >
                          {it.qty}× {it.productName}
                          {it.note && (
                            <span
                              style={{
                                color: "var(--text-secondary)",
                                marginLeft: 6,
                              }}
                            >
                              ({it.note})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "var(--space-2)" }}>
                      <div className="num" style={{ fontWeight: 600 }}>
                        {formatARS(s.totalChargedCents)}
                      </div>
                      <Link className="text-action" to={`/sales/${s.id}`} state={{ from: `/customers/${customerId}` }}>
                        Ver detalle
                      </Link>
                    </div>
                  </div>
                </Card>
              );
            }
            if (entry.kind === "collection") {
              return <CollectionHistoryCard collection={entry.data} key={`c${entry.data.id}`} />;
            }
            const m = entry.data;
            return (
              <Card className={`account-event account-event--${m.type}`} key={`m${m.id}`} style={{ padding: "var(--space-3) var(--space-4)" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "var(--space-3)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                      <Chip variant="tag-success" size="sm">Cobro cta. cte.</Chip>
                    </div>
                    <div
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "var(--font-size-13)",
                      }}
                    >
                      {new Date(m.occurredAt).toLocaleString("es-AR")}
                    </div>
                    {m.note && (
                      <div
                        style={{
                          fontSize: "var(--font-size-13)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {m.note}
                      </div>
                    )}
                  </div>
                  <div
                    className="num"
                    style={{
                      fontWeight: 600,
                      color: "var(--text-primary)",
                    }}
                  >
                    −{formatARS(m.amountCents)}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <button
        className="account-fab"
        type="button"
        onClick={() => {
          setCollectTargetSaleId(null);
          setCollectSheetOpen(true);
        }}
        aria-label="Cobrar cuenta corriente"
        style={{
          position: "fixed",
          right: "var(--space-4)",
          minWidth: 48,
          minHeight: 48,
          padding: "0 var(--space-5)",
          background: "var(--surface-inverse)",
          color: "var(--text-inverse)",
          border: "1px solid var(--surface-inverse)",
          borderRadius: "var(--radius-pill)",
          fontSize: "var(--font-size-14)",
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--space-2)",
          cursor: "pointer",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <PlusIcon />
        Cobrar
      </button>

      <CollectSheet
        open={collectSheetOpen}
        onClose={() => {
          setCollectSheetOpen(false);
          setCollectTargetSaleId(null);
        }}
        customerId={customerId!}
        pendingSales={pendingSales}
        initialSaleId={collectTargetSaleId}
        onCreated={() => {
          setCollectSheetOpen(false);
          setCollectTargetSaleId(null);
        }}
      />
    </main>
  );
}

function balanceLabelColor(cents: number): string {
  if (cents > 0) return "var(--color-warning)";
  if (cents < 0) return "var(--color-info)";
  return "var(--color-success)";
}

function Small({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div
        style={{
          color: "var(--text-secondary)",
          fontSize: "var(--font-size-12)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div className="num" style={{ fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function CollectionHistoryCard({ collection }: { collection: AccountCollection }) {
  const reversed = collection.status === "reverted";
  return (
    <Card className={`account-event account-collection-history${reversed ? " is-reverted" : ""}`} style={{ padding: "var(--space-3) var(--space-4)", opacity: reversed ? 0.62 : 1 }}>
      <div className="account-collection-history__heading">
        <div>
          <div className="account-collection-history__title">
            <Chip variant={reversed ? "tag-danger" : "tag-success"} size="sm">{reversed ? "Cobro revertido" : "Cobro registrado"}</Chip>
            {collection.status === "mixed" && <Chip variant="tag-warning" size="sm">Estado mixto</Chip>}
          </div>
          <p>{new Date(collection.occurredAt).toLocaleString("es-AR")} · {collection.operator.name}</p>
          {collection.note && <p>{collection.note}</p>}
        </div>
        <strong className="num">{formatCollectionAmount(collection.debtCancelledCents)}</strong>
      </div>
      <dl className="account-collection-history__totals">
        <div><dt>Recibido</dt><dd className="num">{formatCollectionAmount(collection.customerPaidCents)}</dd></div>
        <div><dt>Descuento</dt><dd className="num">{formatCollectionAmount(collection.discountCents)}</dd></div>
        <div><dt>Reduce deuda</dt><dd className="num">{formatCollectionAmount(collection.debtCancelledCents)}</dd></div>
      </dl>
      <div className="account-collection-history__lines">
        {collection.lines.map((line) => (
          <div className="account-collection-history__line" key={line.id}>
            <div>
              <strong>{line.paymentMethod.name}</strong>
              <span>Recibido {formatCollectionAmount(line.customerPaidCents)} · descuento {formatCollectionAmount(line.discountCents)}</span>
              {line.allocations.length > 0 && <span>Ventas {line.allocations.map((allocation) => `#${allocation.saleId}`).join(", ")}</span>}
              {line.discountReason && <span>Motivo: {line.discountReason}</span>}
              {line.reversalReason && <span>Reversión: {line.reversalReason}</span>}
            </div>
            <span className="account-collection-history__state">
              {line.status === "reverted" ? "Revertido" : line.settlesInBatch && line.batchSettlement?.status !== "active" ? "Pendiente de liquidación" : line.netReceivedCents === null && line.paymentMethod.requiresNetReceived ? "Pendiente de neto" : `Neto ${formatCollectionAmount(line.netReceivedCents ?? 0)}`}
            </span>
          </div>
        ))}
      </div>
      {!reversed && (collection.pendingNetCents > 0 || collection.pendingSettlementCents > 0) && (
        <p className="account-collection-history__pending">
          {collection.pendingNetCents > 0 && <>Neto pendiente: {formatCollectionAmount(collection.pendingNetCents)}. </>}
          {collection.pendingSettlementCents > 0 && <>Pendiente de liquidación: {formatCollectionAmount(collection.pendingSettlementCents)}.</>}
        </p>
      )}
    </Card>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function CollectSheet({ open, onClose, customerId, pendingSales, initialSaleId, onCreated }: {
  open: boolean; onClose: () => void; customerId: number;
  pendingSales: CustomerAccount["pendingSales"]; initialSaleId: number | null; onCreated: () => void;
}) {
  const methodsQ = usePaymentMethods();
  const create = useCreateAccountSettlement();
  const initialTarget = collectionTargetForSale(initialSaleId, pendingSales);
  const [rows, setRows] = useState<CollectionLineDraft[]>(() => [emptyCollectionLine(initialTarget)]);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const methods = (methodsQ.data ?? []).filter((method) => !method.requiresCustomer);
  const update = (id: string, patch: Partial<CollectionLineDraft>) =>
    setRows((current) => current.map((row) => row.id === id ? { ...row, ...patch } : row));
  const reset = () => {
    setRows([emptyCollectionLine(collectionTargetForSale(initialSaleId, pendingSales))]);
    setNote("");
    setError(null);
  };
  useEffect(() => {
    if (!open) return;
    setRows([emptyCollectionLine(collectionTargetForSale(initialSaleId, pendingSales))]);
    setNote("");
    setError(null);
  }, [open, initialSaleId, pendingSales]);
  const chooseTarget = (row: CollectionLineDraft, saleId: number | null) => {
    const target = collectionTargetForSale(saleId, pendingSales);
    update(row.id, { target, completedDiscountCents: null, manualDiscount: "", reason: "" });
  };
  const chooseAdvancedTarget = (row: CollectionLineDraft) => {
    update(row.id, { target: allocationTarget(pendingSales), completedDiscountCents: null, manualDiscount: "", reason: "" });
  };
  const updateAllocation = (row: CollectionLineDraft, saleId: number, patch: { customerPaid?: string; discount?: string }) => {
    if (row.target.kind !== "allocations") return;
    update(row.id, {
      target: {
        kind: "allocations",
        allocations: row.target.allocations.map((allocation) => allocation.saleId === saleId ? { ...allocation, ...patch } : allocation),
      },
    });
  };
  const submit = async () => {
    setError(null);
    const lines = rows.map((row) => {
      const method = methods.find((candidate) => candidate.id === row.paymentMethodId);
      const result = previewCollectionLine(row, method);
      return { row, method, result, allocationResult: evaluateCollectionAllocations(row, result.preview) };
    });
    if (lines.some(({ method, result }) => !method || !result.preview || result.error)) return setError("Completá medio e importe válidos en cada línea.");
    if (lines.some(({ row, result }) => result.preview!.requiresManualReason && !row.reason.trim())) return setError("Todo descuento manual requiere un motivo.");
    if (lines.some(({ allocationResult }) => allocationResult.error)) return setError(lines.find(({ allocationResult }) => allocationResult.error)!.allocationResult.error!);
    const allocatedBySale = new Map<number, number>();
    for (const { allocationResult } of lines) for (const allocation of allocationResult.allocations) {
      allocatedBySale.set(allocation.saleId, (allocatedBySale.get(allocation.saleId) ?? 0) + allocation.customerPaidAppliedCents + allocation.discountCents);
    }
    if (pendingSales.some((sale) => (allocatedBySale.get(sale.saleId) ?? 0) > sale.pendingCents)) return setError("La suma de las líneas supera el pendiente de una venta.");
    try {
      await create.mutateAsync({ customerId, body: {
        note: note.trim() || null,
        lines: lines.map(({ row, method, result, allocationResult }) => {
          const preview = result.preview!;
          return {
            paymentMethodId: method!.id,
            customerPaidCents: preview.customerPaidCents,
            ...(method!.requiresNetReceived
              ? preview.netReceivedCents === null
                ? { skipNetReceived: true }
                : { netReceivedCents: preview.netReceivedCents }
              : {}),
            discountCents: preview.discountCents,
            discountSource: preview.discountSource,
            discountReason: preview.requiresManualReason ? row.reason.trim() : null,
            allocations: allocationResult.allocations,
          };
        }),
      }});
      reset(); onCreated();
    } catch (cause) { setError((cause as Error).message); }
  };
  return <Sheet open={open} onClose={() => { reset(); onClose(); }} title="Cobrar cuenta corriente">
    <div className="account-collection-sheet">
      <div className="account-collection-sheet__intro">
        <strong>Registrar cobro</strong>
        <p>Una entrega general se distribuye por antigüedad. Elegí una venta cuando quieras aplicar un descuento o completar un saldo puntual.</p>
      </div>
      {rows.map((row, index) => {
        const method = methods.find((candidate) => candidate.id === row.paymentMethodId);
        const { preview, error: previewError } = previewCollectionLine(row, method);
        return <section className={`split-payment-method account-collection-line${method ? ` payment-${method.color}` : ""}`} key={row.id}>
          <div className="split-payment-method__heading"><span>{method ? <>{accountPaymentMethodIcon(method)}<strong>{method.name}</strong></> : <strong>Línea {index + 1}</strong>}</span>{rows.length > 1 && <button type="button" onClick={() => setRows((current) => current.filter((candidate) => candidate.id !== row.id))}>Quitar</button>}</div>
          {!method && <div className="account-collection-methods">{methods.map((option) => <PaymentMethodButton key={option.id} name={option.discountPct ? `${option.name} (${option.discountPct}%)` : option.name} color={accountPaymentButtonColor(option)} icon={accountPaymentMethodIcon(option)} onClick={() => update(row.id, { paymentMethodId: option.id, completedDiscountCents: null, manualDiscount: "", reason: "" })} />)}</div>}
          {method && <>
            <label className="field-control"><span>Destino</span><select value={row.target.kind === "sale" ? row.target.saleId : row.target.kind === "allocations" ? "advanced" : ""} onChange={(event) => event.target.value === "advanced" ? chooseAdvancedTarget(row) : chooseTarget(row, event.target.value ? Number(event.target.value) : null)}><option value="">Entrega general (por antigüedad)</option>{pendingSales.map((sale) => <option key={sale.saleId} value={sale.saleId}>Venta #{sale.saleId} · pendiente {formatCollectionAmount(sale.pendingCents)}</option>)}{pendingSales.length > 1 && <option value="advanced">Distribuir entre varias ventas…</option>}</select></label>
            {row.target.kind === "sale" && <div className="account-collection-target"><span>Venta #{row.target.saleId} · pendiente <strong className="num">{formatCollectionAmount(row.target.pendingCents)}</strong></span><button type="button" onClick={() => update(row.id, completeCollectionLine(row, method))}>Completar</button></div>}
            <MoneyInput label="Recibido" aria-label={`Recibido línea ${index + 1}`} placeholder="$ 0" value={row.customerPaid} onChange={(event) => update(row.id, { customerPaid: event.target.value, completedDiscountCents: null })} />
            {method.requiresNetReceived && <MoneyInput label="Neto acreditado" aria-label={`Neto acreditado línea ${index + 1}`} placeholder="$ 0 (opcional)" helpText="Si todavía no lo conocés, podés informarlo después." value={row.netReceived} onChange={(event) => update(row.id, { netReceived: event.target.value })} />}
            {row.target.kind !== "general" && <details className="account-collection-discount"><summary>Modificar descuento</summary><MoneyInput label="Descuento excepcional" aria-label={`Descuento manual línea ${index + 1}`} placeholder="$ 0" value={row.manualDiscount} onChange={(event) => update(row.id, { manualDiscount: event.target.value })} />{row.manualDiscount.trim() && <Input aria-label={`Motivo del descuento línea ${index + 1}`} value={row.reason} onChange={(event) => update(row.id, { reason: event.target.value })} placeholder="Motivo obligatorio del descuento excepcional" />}</details>}
            {row.target.kind === "allocations" && <div className="account-collection-allocations">
              <div><strong>Distribución avanzada</strong><p>Asigná toda esta línea de forma visible. Las sumas deben coincidir con el resumen.</p></div>
              {row.target.allocations.map((allocation) => <div className="account-collection-allocation" key={allocation.saleId}>
                <div><strong>Venta #{allocation.saleId}</strong><span>Pendiente {formatCollectionAmount(allocation.pendingCents)}</span></div>
                <MoneyInput label="Recibido" aria-label={`Recibido venta ${allocation.saleId}, línea ${index + 1}`} placeholder="$ 0" value={allocation.customerPaid} onChange={(event) => updateAllocation(row, allocation.saleId, { customerPaid: event.target.value })} />
                <MoneyInput label="Descuento" aria-label={`Descuento venta ${allocation.saleId}, línea ${index + 1}`} placeholder="$ 0" value={allocation.discount} onChange={(event) => updateAllocation(row, allocation.saleId, { discount: event.target.value })} />
              </div>)}
            </div>}
          </>}
          {previewError && <p className="account-collection-line__error" role="alert">{previewError}</p>}
          <dl className="account-collection-preview"><div><dt>Recibido</dt><dd className="num">{preview ? formatCollectionAmount(preview.customerPaidCents) : "—"}</dd></div><div><dt>Descuento</dt><dd className="num">{preview ? formatCollectionAmount(preview.discountCents) : "—"}</dd></div><div><dt>Reduce deuda en</dt><dd className="num">{preview ? formatCollectionAmount(preview.debtCancelledCents) : "—"}</dd></div></dl>
          {method && preview && <p className="account-collection-recognition">{preview.recognition === "batch" ? "Pendiente de liquidación por lote." : preview.recognition === "pending_net" ? "Neto y comisión pendientes de informar." : method.requiresNetReceived ? `Neto: ${formatCollectionAmount(preview.netReceivedCents ?? 0)} · Comisión: ${formatCollectionAmount(preview.commissionCents)}` : `Neto inmediato: ${formatCollectionAmount(preview.netReceivedCents ?? 0)}`}</p>}
        </section>;
      })}
      <CollectionSummary rows={rows} methods={methods} />
      <Button type="button" variant="outline" onClick={() => setRows((current) => [...current, emptyCollectionLine()])}>+ Agregar medio</Button>
      <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Nota (opcional)" />
      {error && <p role="alert" style={{ color: "var(--color-danger)", margin: 0 }}>{error}</p>}
      <Button type="button" variant="primary" onClick={submit} disabled={create.isPending}>{create.isPending ? "Registrando…" : "Registrar cobro"}</Button>
    </div>
  </Sheet>;
}

function CollectionSummary({ rows, methods }: { rows: CollectionLineDraft[]; methods: PaymentMethod[] }) {
  const total = rows.reduce((current, row) => {
    const { preview } = previewCollectionLine(row, methods.find((method) => method.id === row.paymentMethodId));
    if (!preview) return current;
    return {
      received: current.received + preview.customerPaidCents,
      discount: current.discount + preview.discountCents,
      debt: current.debt + preview.debtCancelledCents,
      immediateNet: current.immediateNet + (preview.recognition === "immediate" ? (preview.netReceivedCents ?? 0) : 0),
      pendingNet: current.pendingNet + (preview.recognition === "pending_net" ? preview.customerPaidCents : 0),
      pendingBatch: current.pendingBatch + (preview.recognition === "batch" ? preview.customerPaidCents : 0),
    };
  }, { received: 0, discount: 0, debt: 0, immediateNet: 0, pendingNet: 0, pendingBatch: 0 });
  return <section className="account-collection-summary" aria-label="Resumen del cobro">
    <strong>Resumen del cobro</strong>
    <dl>
      <div><dt>Recibido</dt><dd className="num">{formatCollectionAmount(total.received)}</dd></div>
      <div><dt>Descuento</dt><dd className="num">{formatCollectionAmount(total.discount)}</dd></div>
      <div><dt>Reduce deuda en</dt><dd className="num">{formatCollectionAmount(total.debt)}</dd></div>
      <div><dt>Neto inmediato</dt><dd className="num">{formatCollectionAmount(total.immediateNet)}</dd></div>
      {total.pendingNet > 0 && <div><dt>Neto pendiente</dt><dd className="num">{formatCollectionAmount(total.pendingNet)}</dd></div>}
      {total.pendingBatch > 0 && <div><dt>Por liquidar</dt><dd className="num">{formatCollectionAmount(total.pendingBatch)}</dd></div>}
    </dl>
  </section>;
}

function collectionTargetForSale(saleId: number | null, pendingSales: CustomerAccount["pendingSales"]): CollectionTarget {
  const sale = saleId == null ? undefined : pendingSales.find((candidate) => candidate.saleId === saleId);
  return sale ? { kind: "sale", saleId: sale.saleId, pendingCents: sale.pendingCents } : { kind: "general" };
}

function accountPaymentButtonColor(method: PaymentMethod): "cash" | "transfer" | "card" | "charge" | "magenta" {
  return method.color === "other" ? "charge" : method.color as "cash" | "transfer" | "card" | "magenta";
}

function accountPaymentMethodIcon(method: Pick<PaymentMethod, "icon" | "color">) {
  if (method.icon === "Banknote" || method.color === "cash") return <BanknoteIcon />;
  if (method.icon === "ArrowLeftRight" || method.color === "transfer") return <ArrowLeftRightIcon />;
  if (method.icon === "CreditCard" || method.color === "card") return <CreditCardIcon />;
  if (method.icon === "UserCircle") return <UserIcon />;
  return <NotebookPenIcon />;
}
